import classNames from "classnames";
import React, { useMemo } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import reportSelectors from "redux/app/report/selectors";
import {
	transformORSDataForTable,
	transformVesselSpecificORSDataForTable,
} from "redux/app/report/utils";
import { IStore } from "redux/store";

const orsAllVesselsHeaders = [
	"Year",
	"Quarter",
	"Incinerated",
	"Evaporated",
	"Boiler",
	"Transfer to slop",
	"Disposed ashore",
	"Total ORS",
];

type OwnProps = {};
type ORSTableProps = PropsFromRedux & OwnProps;
function ORSTable(props: ORSTableProps) {
	const { rawOrsData, vesselSpecificRawOrsData, year, vesselId } = props;
	const allVesselTableRowRecords = useMemo(() => {
		return Object.keys(rawOrsData)?.length && !vesselId
			? transformORSDataForTable(rawOrsData)
			: [];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rawOrsData, vesselId]);

	const vesselSpecificTableRowRecords = useMemo(() => {
		return Object.keys(vesselSpecificRawOrsData)?.length && vesselId
			? transformVesselSpecificORSDataForTable(vesselSpecificRawOrsData)
			: [];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vesselSpecificRawOrsData, vesselId]);

	const tableRowRecords = vesselId
		? vesselSpecificTableRowRecords
		: allVesselTableRowRecords;

	const handleExportCSV = () => {
		if (!tableRowRecords || !tableRowRecords.length) return;

		try {
			// Generate CSV content
			const rows = tableRowRecords.map((row) => {
				const values = [
					year,
					row.quarter,
					row.incinerated.toFixed(2),
					row.evaporated.toFixed(2),
					row.boiler.toFixed(2),
					row.transferToSlop.toFixed(2),
					row.disposedAshore.toFixed(2),
					row.quarterTotal.toFixed(2),
				];

				// Handle special characters and format values
				return values
					.map((val) => {
						if (val === null || val === undefined) return "";
						const str = String(val);
						// Escape quotes and wrap in quotes if contains comma or newline
						const escaped = str.replace(/"/g, '""');
						return /[,\n"]/.test(escaped) ? `"${escaped}"` : escaped;
					})
					.join(",");
			});

			// Combine headers and rows
			const csvContent = [orsAllVesselsHeaders.join(","), ...rows].join("\n");

			// Create and trigger download
			const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
			const link = document.createElement("a");
			const url = URL.createObjectURL(blob);

			// Set filename with current date for better tracking
			const date = new Date().toISOString().split("T")[0];
			const filename = `ors_${
				vesselId ? vesselId + "_" : ""
			}${year}_${date}.csv`;

			link.setAttribute("href", url);
			link.setAttribute("download", filename);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		} catch (error) {
			console.error("Error exporting CSV:", error);
			alert("Failed to export data. Please try again.");
		}
	};

	return (
		<div className="px-4">
			<div className="flex justify-end mb-4">
				<button
					onClick={handleExportCSV}
					className="p-3 px-6 py-1 mt-1 mr-1 text-base text-indigo-700 border border-indigo-600 rounded-sm outline-none ws-button-og hover:bg-indigo-50 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
					disabled={!tableRowRecords || tableRowRecords.length === 0}
					title={
						!tableRowRecords || tableRowRecords.length === 0
							? "No data to export"
							: "Export data as CSV"
					}
				>
					<span className="mr-3 text-sm">Export Data</span>
					<span className="mb-0.5 text-sm bp3-icon bp3-icon-download"></span>
				</button>
			</div>
			<h2 className="text-xl font-medium mb-3">ORS (m³)</h2>{" "}
			<table className="w-full h-full border border-gray-400 table-auto rounded-md font-redhat-text  overflow-auto  border-collapse text-center">
				<thead>
					<tr>
						{orsAllVesselsHeaders.map((headerLabel) => (
							<th
								key={headerLabel}
								className="px-3 py-3 text-sm border font-medium leading-4 tracking-wide text-center text-gray-700 bg-gray-100 border-b border-gray-300"
							>
								{headerLabel}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{tableRowRecords.map((row, index) => {
						const {
							quarter,
							incinerated,
							evaporated,
							boiler,
							transferToSlop,
							disposedAshore,
							quarterTotal,
						} = row;
						return (
							<tr
								key={row.quarter}
								className={classNames({
									"font-medium": index === tableRowRecords.length - 1,
								})}
							>
								{index === 0 && (
									<td
										rowSpan={5}
										className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]"
									>
										{year}
									</td>
								)}
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{quarter}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{incinerated.toFixed(2)}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{evaporated.toFixed(2)}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{boiler.toFixed(2)}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{transferToSlop.toFixed(2)}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{disposedAshore.toFixed(2)}
								</td>
								<td className="px-1 text-center py-3 text-[8px] lg:text-xs xl:text-[12px] border leading-4 tracking-wide border-b border-gray-300 font-redhat-text text-[#2D2D2D]">
									{quarterTotal.toFixed(2)}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	const reportStore = reportSelectors._getStore(store);
	const rawOrsData = reportSelectors.getRawOrsData(reportStore);
	const vesselSpecificRawOrsData =
		reportSelectors.getRawVesselSpecificOrsData(reportStore);
	const year = reportSelectors.getOrsYear(reportStore);
	const vesselId = reportSelectors.getOrsVesselId(reportStore);

	return {
		rawOrsData,
		vesselSpecificRawOrsData,
		year,
		vesselId,
	};
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(ORSTable);
